import { useEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import { FormattedMessage, useIntl } from 'react-intl';

import { INSIGHT_FAQ_ITEMS } from './insightFaqData';

const MIN_SEARCH_CHARS = 3;

function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Split text and wrap case-insensitive matches in a highlighted mark (when needle has ≥3 chars). */
function highlightMatches(text, needle, theme, keyPrefix = 'm') {
  if (text == null || text === '') return text;
  const n = (needle || '').trim();
  if (n.length < MIN_SEARCH_CHARS) return text;
  try {
    const re = new RegExp(`(${escapeRegExp(n)})`, 'gi');
    const parts = String(text).split(re);
    return parts.map((part, i) => {
      if (part.toLowerCase() === n.toLowerCase()) {
        return (
          <Box
            key={`${keyPrefix}-h-${i}`}
            component="mark"
            sx={{
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 213, 79, 0.38)' : 'rgba(255, 235, 59, 0.75)',
              color: 'inherit',
              padding: '0 2px',
              borderRadius: 0.5
            }}
          >
            {part}
          </Box>
        );
      }
      return part;
    });
  } catch {
    return text;
  }
}

export default function InsightFaqDialog({ open, onClose }) {
  const theme = useTheme();
  const intl = useIntl();

  const [search, setSearch] = useState('');
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const searchTrimmed = search.trim();
  const searchActive = searchTrimmed.length >= MIN_SEARCH_CHARS;

  const filteredItems = useMemo(() => {
    if (searchTrimmed.length < MIN_SEARCH_CHARS) {
      return INSIGHT_FAQ_ITEMS;
    }
    const needle = searchTrimmed.toLowerCase();
    return INSIGHT_FAQ_ITEMS.filter((item) => {
      const q = intl.formatMessage({ id: item.qId, defaultMessage: item.defaultQ }).toLowerCase();
      const a = intl.formatMessage({ id: item.aId, defaultMessage: item.defaultA }).toLowerCase();
      return q.includes(needle) || a.includes(needle);
    });
  }, [searchTrimmed, intl]);

  // Auto-expand accordions when the keyword appears in the answer (search ≥3 chars).
  useEffect(() => {
    if (searchTrimmed.length < MIN_SEARCH_CHARS) {
      setExpandedIds(INSIGHT_FAQ_ITEMS.length > 0 ? new Set([INSIGHT_FAQ_ITEMS[0].qId]) : new Set());
      return;
    }
    const needle = searchTrimmed.toLowerCase();
    const next = new Set();
    filteredItems.forEach((item) => {
      const answerText = intl.formatMessage({ id: item.aId, defaultMessage: item.defaultA });
      if (answerText.toLowerCase().includes(needle)) {
        next.add(item.qId);
      }
    });
    setExpandedIds(next);
  }, [searchTrimmed, filteredItems, intl]);

  const handleAccordionChange = (qId) => (_e, isExpanded) => {
    setExpandedIds((prev) => {
      const n = new Set(prev);
      if (isExpanded) n.add(qId);
      else n.delete(qId);
      return n;
    });
  };

  const handleClose = () => {
    setSearch('');
    setExpandedIds(INSIGHT_FAQ_ITEMS.length > 0 ? new Set([INSIGHT_FAQ_ITEMS[0].qId]) : new Set());
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="100%"
      hideBackdrop
      fullWidth
      scroll="paper"
      aria-labelledby="insight-faq-dialog-title"
      PaperProps={{
        sx: {
          borderRadius: 2,
          bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'background.default',
          p: 0,
          mx: 2,
          mt: { xs: '48px', md: '113px' },
          width: '100%',
          height: 'calc(100% - 80px)',
          maxHeight: '100%'
        }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2.5,
          pt: 2.5,
          pb: 3,
          background:
            theme.palette.mode === 'dark'
              ? 'linear-gradient(270deg, #17161B 30.57%, #00A78C 203.8%), #FFF'
              : 'linear-gradient(270deg, #FFF 0%, #E0F2F1 100%), #FFF'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexDirection: 'column' }}>
          <Typography variant="h1" sx={{ fontWeight: 500, fontSize: '32px' }}>
            <FormattedMessage id="insightFaqMainTitle" defaultMessage="Netgear Insight FAQ" />
          </Typography>
          <Typography id="insight-faq-dialog-title" variant="h3" sx={{ fontWeight: 400, color: 'text.primary' }}>
            <FormattedMessage id="insightFaqTitle" defaultMessage="Find answers to common questions about Netgear Insight" />
          </Typography>
        </Box>
        <IconButton
          id="insight-faq-close"
          aria-label={intl.formatMessage({ id: 'close', defaultMessage: 'Close' })}
          onClick={handleClose}
          size="small"
          sx={{ color: 'text.secondary', position: 'absolute', right: 24, top: 24 }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          px: 0,
          pb: 3,
          pt: 2,
          bgcolor: theme.palette.mode === 'dark' ? theme.palette.dark[800] : 'grey.50'
        }}
      >
        <Box
          sx={{
            px: 4,
            pt: 4,
            pb: 2,
            borderRadius: 2,
            bgcolor: theme.palette.background.paper,
            border: 1,
            borderColor: 'divider'
          }}
        >
          <TextField
            id="insight-faq-search"
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={intl.formatMessage({
              id: 'insightFaqSearchPlaceholder',
              defaultMessage: 'Search FAQs...'
            })}
            helperText={
              searchTrimmed.length > 0 && searchTrimmed.length < MIN_SEARCH_CHARS
                ? intl.formatMessage({
                    id: 'insightFaqMinCharsHint',
                    defaultMessage: 'Enter at least 3 characters to search.'
                  })
                : undefined
            }
            FormHelperTextProps={{ id: 'insight-faq-search-helper' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              )
            }}
            inputProps={{
              'aria-describedby':
                searchTrimmed.length > 0 && searchTrimmed.length < MIN_SEARCH_CHARS ? 'insight-faq-search-helper' : undefined
            }}
            sx={{
              mb: 2,
              '& #insight-faq-search': {
                padding: '15px 14px'
              },
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px'
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderRadius: '10px'
              }
            }}
          />

          <Box
            sx={{
              borderRadius: 1,
              maxHeight: 'calc(100vh - 355px)',
              overflow: 'auto',
              '&::-webkit-scrollbar': {
                width: 4
              }
            }}
          >
            {searchActive && filteredItems.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                <FormattedMessage id="insightFaqNoResults" defaultMessage="No FAQs match your search." />
              </Typography>
            ) : filteredItems.length > 0 ? (
              filteredItems.map((item) => {
                const questionText = intl.formatMessage({ id: item.qId, defaultMessage: item.defaultQ });
                const answerText = intl.formatMessage({ id: item.aId, defaultMessage: item.defaultA });
                return (
                  <Accordion
                    key={item.qId}
                    expanded={expandedIds.has(item.qId)}
                    onChange={handleAccordionChange(item.qId)}
                    disableGutters
                    elevation={0}
                    id={`${item.qId}-accordion`}
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: '10px',
                      mb: 1,
                      '&:last-of-type': { mb: 0 },
                      ' .MuiAccordionSummary-content': {
                        my: 2.3
                      }
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="body2" component="div" sx={{ fontWeight: 500, color: 'grey.800', pr: 1 }}>
                        {searchActive ? highlightMatches(questionText, searchTrimmed, theme, `${item.qId}-q`) : questionText}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{
                        pt: 2,
                        pb: 2,
                        px: 2,
                        borderTop: 1,
                        borderColor: 'divider'
                      }}
                    >
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        component="div"
                        id={`${item.qId}-answer`}
                        sx={{
                          lineHeight: '22px',
                          whiteSpace: 'pre-line'
                        }}
                      >
                        {searchActive ? highlightMatches(answerText, searchTrimmed, theme, `${item.qId}-a`) : answerText}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                );
              })
            ) : null}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
